import { createFileRoute, notFound } from "@tanstack/react-router"
import { FileSpreadsheet, Smartphone } from "lucide-react"

import { breadcrumbJsonLd, buildHead, webPageJsonLd } from "@/core/seo"

import { CompareHeader, Section, linkClass } from "../components/compare/compare-parts"
import { formatCheckedOn } from "../components/home/home-content"
import { AnswerBox, Breadcrumb, CtaBand, SimpleTable, SourceLine } from "../components/landing"
import { PickPair } from "../components/landing/page-parts"
import { COMPARE_PUBLISHED, compareReviewed } from "../content/compare/publish"
import { SPREADSHEET_SOURCE } from "../content/compare/spreadsheet"

const PATH = "/compare/spreadsheet-logbook"

/**
 * Copy source: docs/design/landing/copy-compare.md section 3. No competitor cells: the spreadsheet
 * column says "By hand" and makes no claim about spreadsheet software. ATO facts match /logbook-method.
 */
export const spreadsheetSeo = () =>
  buildHead({
    title: "Spreadsheet logbook vs a logbook app",
    description:
      "Keeping the 12-week ATO logbook in a spreadsheet or on paper versus an app: what you must record, what takes time, and when a spreadsheet is enough.",
    path: PATH,
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Compare", path: "/compare" },
        { name: "Spreadsheet logbook" },
      ]),
      webPageJsonLd({
        name: "Spreadsheet logbook or logbook app?",
        path: PATH,
        dateModified: compareReviewed[PATH],
      }),
    ],
  })

export const Route = createFileRoute("/_brand-layout/compare/spreadsheet-logbook")({
  beforeLoad: () => {
    if (!COMPARE_PUBLISHED) throw notFound()
  },
  head: () => {
    const { meta, links, scripts } = spreadsheetSeo()
    return { meta, links, scripts }
  },
  component: SpreadsheetPage,
})

const ROWS = [
  {
    header: "Recording each trip",
    cells: [
      "By hand: date, kilometres and reason, each trip",
      "Trips can be recorded by your phone for you to check",
    ],
  },
  {
    header: "Odometer readings",
    cells: [
      "By hand, at the start and end of the period",
      "Kept for each vehicle next to your trips",
    ],
  },
  {
    header: "Business and personal trips",
    cells: [
      "By hand: mark each trip and add up the kilometres",
      "Each trip is marked business or personal in the app",
    ],
  },
  {
    header: "Cost",
    cells: [
      "Your time",
      <a key="p" href="/pricing" className={linkClass}>
        See our pricing page
      </a>,
    ],
  },
]

export function SpreadsheetPage() {
  const reviewed = compareReviewed[PATH]
  const checked = formatCheckedOn(SPREADSHEET_SOURCE.checkedAt)
  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Compare", href: "/compare" },
          { label: "Spreadsheet logbook" },
        ]}
      />
      <CompareHeader reviewed={reviewed} title="Spreadsheet logbook or logbook app?">
        <AnswerBox
          source={
            <SourceLine
              sources={[{ label: SPREADSHEET_SOURCE.label, href: SPREADSHEET_SOURCE.url }]}
              checked={checked}
            />
          }
        >
          A spreadsheet works if you can fill it in the same day for 12 weeks. An app helps if you
          forget trips. Either way, the ATO&apos;s rules decide what the record must contain.
        </AnswerBox>
      </CompareHeader>
      <Section title="Side by side">
        <SimpleTable
          caption="Spreadsheet or paper compared with Kestly"
          columns={["", "Spreadsheet or paper", "Kestly"]}
          rows={ROWS}
        />
        <p className="text-muted-foreground text-sm">
          The ATO logbook method needs a logbook kept for at least 12 continuous weeks.{" "}
          <a href={SPREADSHEET_SOURCE.url} className={linkClass}>
            ATO, logbook method
          </a>
          , checked {checked}. We make no claim about any particular spreadsheet software.
        </p>
      </Section>
      <Section title="More on the method">
        <p>
          See{" "}
          <a href="/logbook-method" className={linkClass}>
            how the logbook method works
          </a>
          , or the{" "}
          <a href="/mileage-tracker" className={linkClass}>
            mileage tracker
          </a>
          .
        </p>
      </Section>
      <Section title="Who should pick which">
        <PickPair
          items={[
            {
              icon: FileSpreadsheet,
              tone: "success",
              title: "Pick a spreadsheet if",
              body: "You drive few trips and keep notes daily.",
            },
            {
              icon: Smartphone,
              tone: "primary",
              title: "Pick an app if",
              body: "You drive often or forget.",
            },
          ]}
        />
      </Section>
      <CtaBand
        title="See it with your own trips"
        description="Create a free account and record your first trips."
        ctaLabel="Create a free account"
        ctaHref="/login"
      />
    </>
  )
}
